<template>
    <console-layout width="100%" max-width="1280">

        <console-title>
            <span>이벤트 게시판</span>
        </console-title>

        <console-search @search="page=1; search(true)">
            <v-row>
                <v-col cols="12" sm="3">
                    <v-select v-model="filter.status" :items="statusItems" item-text="text" item-value="value" label="상태" persistent-placeholder hide-details/>
                </v-col>
                <v-col cols="12" sm="3">
                    <v-select v-model="filter.searchKey" :items="searchKeys" item-text="text" item-value="value" label="검색조건" persistent-placeholder hide-details @input="filter.searchValue = null;" />
                </v-col>
                <v-col cols="12" sm="3">
                    <v-text-field v-model="filter.searchValue" label="검색어" persistent-placeholder hide-details :disabled="!filter.searchKey" @keydown.enter="page = 1; search(true)" />
                </v-col>
            </v-row>
        </console-search>

        <console-body>
            <v-layout class="align-end">
                <span cols="auto" class="subtitle-2">검색수 : {{ summary.totalCount.format() }}</span>
                <v-spacer />
                <span cols="auto">
                    <v-btn color="white" elevation="1" class="green--text px-2 ml-1" @click="excel">
                        <v-icon class="mr-2">mdi-microsoft-excel</v-icon>엑셀다운로드
                    </v-btn>
                </span>
            </v-layout>
    
            <v-data-table :items="boards" :headers="boardsHeaders" hide-default-footer disable-sort disable-pagination class="mt-2 elevation-1">
                <template slot="item.createdAt" slot-scope="{item}">
                    {{ $dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") }}
                </template>
                <template slot="item.thumb" slot-scope="{item}">
                    <v-img :src="item.thumb" max-width="96" height="96" contain class="ma-auto"/>
                </template>
                <template slot="item.status" slot-scope="{item}">
                    <span style="color: #E53935">{{ getActualStatus(item) }}</span>
                    <div>{{ EVENT_STATUS_ITEMS[item.status].text }}</div>
                </template>
                <template slot="item.eventDate" slot-scope="{item}">
                    {{ item.display.startDate }} ~ {{ item.display.endDate || "상시진행" }}
                </template>
                <template slot="item.actions" slot-scope="{item}">
                    <v-icon small class="mx-1" @click="$router.push(`/console/boards/event/${item._id}`)">mdi-pencil</v-icon>
                    <v-icon v-if="$hasScope(['root', 'admin'])" small class="mx-1" @click="remove(item)">mdi-delete</v-icon>
                </template>
            </v-data-table>
    
            <v-pagination v-model="page" :length="pageCount" total-visible="11" class="mt-3" @input="search(true)"/>
        </console-body>

        <v-btn v-if="$hasScope(['root', 'admin'])" fab fixed bottom right color="accent" to="/console/boards/event/create"><v-icon>mdi-pencil</v-icon></v-btn>

    </console-layout>
</template>

<script>
import api from "@/api";
import XLSX from "xlsx";
import ConsoleContent from "@/templates/console/ConsoleContent.vue";
import { mdiMicrosoftExcel } from "@mdi/js";
import { EVENT_STATUS_ITEMS } from "./View.vue";

export default {
    components: {
        ConsoleContent
    },
    data(){
        return {
            mdiMicrosoftExcel,
            EVENT_STATUS_ITEMS,

            showSearch: true,

            filter: {
                searchKey: this.$route.query.searchKey || null,
                searchValue: this.$route.query.searchValue || null,
                status: this.$route.query.status || null
            },

            page: +this.$route.query.page || 1,
            pageCount: 0,
            limit: 10,

            summary: { totalCount: 0 },
            boards: [],
            boardsHeaders: [
                { text: "제목", value: "subject", align: "center", width: 400 },
                { text: "썸네일", value: "thumb", align: "center", width: 100 },
                { text: "작성자", value: "writer.name", align: "center", width: 100 },
                { text: "진행일", value: "eventDate", align: "center", width: 200 },
                { text: "상태", value: "status", align: "center", width: 120 },
                { text: "작성일자", value: "createdAt", align: "center", width: 200 },
                { text: "actions", value: "actions", align: "center", width: 100 },
            ],

            searchKeys: [
                { text: ":: 전체 ::", value: null },
            ],
            statusItems: [
                { text: ":: 전체 ::", value: null },
                ... Object.values(EVENT_STATUS_ITEMS)
            ],
        }
    },
    mounted(){
        this.init();
    },
    methods: {
        init(){
            this.search(false);
        },
        async search(routable = false){
            if(routable){
                this.$router.push({
                    query: { ...this.filter, page: this.page }
                });
            }

            let { summary, boards } = await api.console.boards.gets({
                headers: {
                    code: "event",
                    skip: (this.page - 1) * this.limit,
                    limit: this.limit
                },
                params: this.filter
            });

            this.boards = boards;
            this.summary = summary;
            this.pageCount = Math.ceil(summary.totalCount / this.limit);
        },
        async remove(board){
            if(confirm("삭제하시겠습니까?")){
                await api.console.boards.delete(board);
                alert("삭제되었습니다");
                this.search(false);
            }
        },
        getActualStatus(item){
            let { display: { startDate = null, endDate = null }, status } = item;
            if(status === "open"){
                const today = this.$dayjs();
                const formatStartDate = this.$dayjs(startDate).startOf('day');
                const formatEndDate = this.$dayjs(endDate).endOf('day');

                if(today.isBefore(formatStartDate)) return EVENT_STATUS_ITEMS.schedule.text;
                if(today.isAfter(formatStartDate) && today.isBefore(formatEndDate)) return EVENT_STATUS_ITEMS.open.text;
                if(today.isAfter(formatEndDate)) return EVENT_STATUS_ITEMS.close.text;
            };

            return EVENT_STATUS_ITEMS[status].text;
        },
        async excel(){
            let { boards } = await api.console.boards.gets({ headers: { code: "event", limit: 0 }, params: this.filter });

            let rows = [];
            boards.forEach(board => {
                rows.push({
                    "no": board.no,
                    "이름": board.name,
                });
            });

            let workbook = new XLSX.utils.book_new();
            let worksheet = XLSX.utils.json_to_sheet(rows);

            XLSX.utils.book_append_sheet(workbook, worksheet, "엑셀시트이름");
            XLSX.writeFile(workbook, "엑셀파일이름.xlsx");
        }
    }
}
</script>